import API from "./api.js";

/**
 * Submit feedback
 * @param {Object} feedbackData - Feedback data
 * @param {number} feedbackData.rating - Rating (1-5)
 * @param {string} feedbackData.message - Feedback message
 * @param {string} feedbackData.type - Type (Bug, Suggestion, Feedback)
 * @param {File} feedbackData.screenshot - Optional screenshot file
 * @param {Object} feedbackData.deviceInfo - Device information
 * @param {string} token - Optional auth token
 * @returns {Promise<Object>} Response data
 */
export const submitFeedback = async (feedbackData, token = null) => {
  const formData = new FormData();
  
  // Only append rating if provided
  if (feedbackData.rating && feedbackData.rating > 0) {
    formData.append("rating", feedbackData.rating);
  }
  
  formData.append("message", feedbackData.message);
  formData.append("type", feedbackData.type);
  
  // Append optional contact information
  if (feedbackData.contactName) {
    formData.append("contactName", feedbackData.contactName);
  }
  
  if (feedbackData.contactEmail) {
    formData.append("contactEmail", feedbackData.contactEmail);
  }
  
  if (feedbackData.deviceInfo) {
    formData.append("deviceInfo", JSON.stringify(feedbackData.deviceInfo));
  }
  
  if (feedbackData.screenshot) {
    formData.append("screenshot", feedbackData.screenshot);
  }

  const config = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  };

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const response = await API.post("/feedback", formData, config);
  return response.data;
};

/**
 * Get all feedback (Admin only)
 * @param {string} token - Auth token
 * @param {Object} filters - Filter options
 * @returns {Promise<Object>} Response data
 */
export const getAllFeedback = async (token, filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.type) params.append("type", filters.type);
  if (filters.status) params.append("status", filters.status);
  if (filters.rating) params.append("rating", filters.rating);
  if (filters.search) params.append("search", filters.search);
  if (filters.page) params.append("page", filters.page);
  if (filters.limit) params.append("limit", filters.limit);
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

  const response = await API.get(`/feedback?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Get feedback by ID (Admin only)
 * @param {string} id - Feedback ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Response data
 */
export const getFeedbackById = async (id, token) => {
  const response = await API.get(`/feedback/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Update feedback status (Admin only)
 * @param {string} id - Feedback ID
 * @param {Object} updateData - Update data
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Response data
 */
export const updateFeedbackStatus = async (id, updateData, token) => {
  const response = await API.patch(`/feedback/${id}/status`, updateData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Get feedback statistics (Admin only)
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Response data
 */
export const getFeedbackStats = async (token) => {
  const response = await API.get("/feedback/stats", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Capture device information
 * @returns {Object} Device info
 */
export const captureDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  const language = navigator.language;
  const screenResolution = `${window.screen.width}x${window.screen.height}`;
  
  // Detect browser
  let browser = "Unknown";
  if (userAgent.indexOf("Chrome") > -1) browser = "Chrome";
  else if (userAgent.indexOf("Firefox") > -1) browser = "Firefox";
  else if (userAgent.indexOf("Safari") > -1) browser = "Safari";
  else if (userAgent.indexOf("Edge") > -1) browser = "Edge";
  else if (userAgent.indexOf("Opera") > -1) browser = "Opera";
  
  // Detect OS
  let os = "Unknown";
  if (userAgent.indexOf("Win") > -1) os = "Windows";
  else if (userAgent.indexOf("Mac") > -1) os = "macOS";
  else if (userAgent.indexOf("Linux") > -1) os = "Linux";
  else if (userAgent.indexOf("Android") > -1) os = "Android";
  else if (userAgent.indexOf("iOS") > -1) os = "iOS";

  return {
    userAgent,
    platform,
    language,
    screenResolution,
    browser,
    os,
  };
};


